import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import formatOrderId from '../../utils/formatOrderId';

const { FiFilter, FiSearch, FiFileText, FiClock, FiDollarSign, FiUser, FiAlertCircle } = FiIcons;

const AvailableOrders = () => {
  const navigate = useNavigate();
  const { availableOrders, fetchAvailableOrders, acceptOrder, loading, operatorProfile, assignedOrders } = useOperator();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [processingOrder, setProcessingOrder] = useState(null);

  useEffect(() => {
    fetchAvailableOrders();
  }, [fetchAvailableOrders]);

  // Filter orders based on search term
  const filteredOrders = availableOrders.filter(order => {
    return (
      order.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.service_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Sort filtered orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === 'oldest') {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === 'fee_high_to_low') {
      return (b.application_fee + b.service_fee) - (a.application_fee + a.service_fee);
    } else if (sortBy === 'fee_low_to_high') {
      return (a.application_fee + a.service_fee) - (b.application_fee + b.service_fee);
    }
    return 0;
  });

  const handleAcceptOrder = async (orderId) => {
    setProcessingOrder(orderId);
    const result = await acceptOrder(orderId);
    setProcessingOrder(null);

    if (result) {
      navigate(`/operator/order/${orderId}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Available Applications</h1>
          <p className="text-gray-600 mt-2">
            Browse and accept available application orders
          </p>
        </motion.div>

        <Card className="mb-8">
          <div className="p-6">
            {/* Notice: Awaiting admin verification after submission */}
            {Array.isArray(assignedOrders) && assignedOrders.some(o => o.status === 'assigned' || o.status === 'in_progress') && (
              <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <div className="flex">
                  <div className="ml-0">
                    <p className="text-sm text-yellow-700">
                      <span className="font-medium">Complete your current application:</span> Upload the PDF and submit for admin review before accepting another order.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Filters and Search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full md:w-64"
                />
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiFilter} className="text-gray-500" />
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="latest">Latest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="fee_high_to_low">Fee: High to Low</option>
                  <option value="fee_low_to_high">Fee: Low to High</option>
                </select>
              </div>
            </div>

            {/* Orders List */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              </div>
            ) : sortedOrders.length > 0 ? (
              <div className="space-y-6">
                {sortedOrders.map((order) => (
                  <motion.div
                    key={order.order_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="mb-4 md:mb-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <SafeIcon icon={FiFileText} className="w-5 h-5 text-primary-600" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            {order.service_type}
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                          <div>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Order ID:</span> <span className="font-mono">{formatOrderId(order.order_id || order.id)}</span>
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Customer:</span> {order.customer_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Created:</span> {formatDate(order.created_at)}
                            </p>
                            <div className="flex items-center">
                              <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-green-600 mr-1" />
                              <p className="text-sm font-medium text-gray-900">
                                ₹{order.application_fee + order.service_fee}
                              </p>
                              <span className="mx-1 text-gray-500">|</span>
                              <p className="text-xs text-gray-600">
                                App Fee: ₹{order.application_fee} | Service Fee: ₹{order.service_fee}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Button
                          onClick={() => handleAcceptOrder(order.order_id)}
                          loading={processingOrder === order.order_id}
                          disabled={Array.isArray(assignedOrders) && assignedOrders.some(o => o.status === 'assigned' || o.status === 'in_progress')}
                          className="w-full md:w-auto"
                        >
                          {Array.isArray(assignedOrders) && assignedOrders.some(o => o.status === 'assigned' || o.status === 'in_progress') ? 'Finish Current Application' : 'Accept Order'}
                        </Button>
                      </div>
                    </div>

                    {/* Documents Section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Required Documents:</h4>
                      <div className="flex flex-wrap gap-2">
                        {order.documents && Object.keys(order.documents).map((doc) => (
                          <span
                            key={doc}
                            className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full"
                          >
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No available orders found</p>
                <p className="text-sm text-gray-500 mt-2">
                  {searchTerm ? 'Try adjusting your search criteria' : 'Check back later for new orders'}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* How It Works Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">How It Works</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 rounded-full p-3">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">1. Accept an Application</h3>
                  <p className="text-gray-600">
                    Browse available applications and accept one that matches your expertise. Once accepted, it will be assigned to you.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-green-100 rounded-full p-3">
                  <SafeIcon icon={FiUser} className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">2. Contact the Customer</h3>
                  <p className="text-gray-600">
                    Call the customer and share your screen while filling out their application form. Ensure all details are accurate.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-yellow-100 rounded-full p-3">
                  <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">3. Make the Application Payment</h3>
                  <p className="text-gray-600">
                    Pay the application fee from your wallet or via UPI. This amount plus your service fee will be credited back to your wallet after admin verification.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-purple-100 rounded-full p-3">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">4. Submit for Verification</h3>
                  <p className="text-gray-600">
                    Generate the completed PDF and submit it for admin verification. Once verified, you'll receive your payment.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Warning for New Operators */}
        {operatorProfile && !operatorProfile.kyc_verified && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8"
          >
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="font-medium">Complete your profile and KYC verification</span> to start accepting applications.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AvailableOrders;